import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '@/config/config';
import * as Sentry from '@sentry/react';
import { logError } from '@/utils/errorHandler';
import { formStatePersistence } from '@/utils/statePersistence';

// Create the enhanced axios instance with session management
const baseURL = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

const enhancedAxiosInstance = axios.create({
  baseURL: baseURL,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
  },
});

// Track failed requests for retry after token refresh
interface FailedRequest {
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: InternalAxiosRequestConfig;
}

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

// Track inactivity
let inactivityTimer: NodeJS.Timeout | null = null;

// Update activity time on any successful request
const updateActivityTime = () => {
  // Clear and reset inactivity timer
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  // Set timer to detect inactivity (2 minutes)
  inactivityTimer = setTimeout(
    () => {
      // Mark as inactive after 2 minutes
      SessionAwareErrorHandler.isInactive = true;
    },
    2 * 60 * 1000
  );
};

// Helper to process failed queue
const processQueue = (error: any, token?: string) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      resolve(config);
    }
  });

  failedQueue = [];
};

// Session-aware error handler
class SessionAwareErrorHandler {
  private static sessionExpiredShown = false;
  static suppressErrorToasts = false; // Flag to suppress error toasts during session management
  static isInactive = false; // Flag to track if user has been inactive

  static showSessionExpiredDialog(reason: string) {
    if (this.sessionExpiredShown) return;
    this.sessionExpiredShown = true;

    // Import toast dynamically to avoid circular dependencies
    import('sonner').then(({ toast }) => {
      toast.error('Session Expired', {
        duration: 10000,
        description: reason,
        action: {
          label: 'Login',
          onClick: () => {
            window.location.href = '/login';
          },
        },
      });
    });
  }

  static resetSessionState() {
    this.sessionExpiredShown = false;
    this.isInactive = false;
  }

  static shouldSuppressError(error: AxiosError): boolean {
    // Always suppress 401 errors - let the interceptor handle them
    if (error.response?.status === 401) {
      return true;
    }

    // Suppress errors during refresh
    if (this.suppressErrorToasts || isRefreshing) {
      return true;
    }

    // Suppress errors when inactive
    if (this.isInactive) {
      return true;
    }

    // Suppress token refresh endpoint errors
    if (error.config?.url?.includes('/token/refresh/')) {
      return true;
    }

    return false;
  }
}

// Request interceptor
enhancedAxiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Reset inactive state when making a request
    if (SessionAwareErrorHandler.isInactive) {
      SessionAwareErrorHandler.resetSessionState();
    }

    // Ensure headers exist
    if (!config.headers) {
      config.headers = {} as any;
    }

    // Always set Accept header
    config.headers['Accept'] = 'application/json';

    // Set auth token if available
    const token = localStorage.getItem('access_token');
    if (token) {
      const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      config.headers.Authorization = authHeader;
    }

    // Add request metadata for debugging
    config.metadata = {
      startTime: Date.now(),
      requestId: Math.random().toString(36).substr(2, 9),
    };

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor with session-aware error handling
enhancedAxiosInstance.interceptors.response.use(
  response => {
    // Calculate request duration
    const duration = response.config.metadata?.startTime
      ? Date.now() - response.config.metadata.startTime
      : 0;

    // Log slow requests (>2 seconds)
    if (duration > 2000) {
      logError(
        'Slow API request detected',
        {
          component: 'enhancedAxiosInstance',
          action: 'responseInterceptor',
          metadata: {
            url: response.config.url,
            method: response.config.method,
            duration,
            status: response.status,
          },
        },
        {
          showToast: false, // Don't show toast to users
          captureInSentry: true, // Still capture in Sentry for monitoring
          userFacing: false, // This is not a user-facing error
        }
      );
    }

    // Update activity time
    updateActivityTime();

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      metadata?: any;
    };

    // Enhanced error logging with request context
    const errorContext = {
      url: originalRequest?.url ?? 'unknown',
      method: originalRequest?.method?.toUpperCase() ?? 'UNKNOWN',
      status: error.response?.status,
      statusText: error.response?.statusText,
      requestId: originalRequest?.metadata?.requestId,
      duration: originalRequest?.metadata?.startTime
        ? Date.now() - originalRequest.metadata.startTime
        : undefined,
      requestHeaders: originalRequest?.headers,
      responseData: error.response?.data,
      timestamp: new Date().toISOString(),
      hasAuthToken: !!localStorage.getItem('access_token'),
    };

    // Only log error if it shouldn't be suppressed
    if (!SessionAwareErrorHandler.shouldSuppressError(error)) {
      // Log error with context
      logError(
        'API request failed',
        {
          component: 'enhancedAxiosInstance',
          action: 'responseInterceptor',
          metadata: errorContext,
        },
        {
          showToast: false, // Don't show technical error to users
          captureInSentry: true, // Capture for developers
          userFacing: false, // This is a technical error
        }
      );

      // Sentry error capture with rich context
      try {
        Sentry.setContext('api_error', errorContext);
        Sentry.setTag('api_error_type', 'enhanced_axios');
        Sentry.setTag('api_method', errorContext.method);
        Sentry.setTag('api_status', errorContext.status?.toString() ?? 'unknown');
        Sentry.setTag('api_endpoint', errorContext.url.split('?')[0]);

        Sentry.captureException(error, {
          level: 'error',
          tags: {
            error_type: 'api_error',
            api_endpoint: errorContext.url.split('?')[0],
            api_status: errorContext.status?.toString() ?? 'unknown',
            api_method: errorContext.method,
          },
          extra: errorContext,
        });
      } catch (sentryError) {
        logError('Failed to capture error in Sentry', {
          component: 'enhancedAxiosInstance',
          action: 'sentryCapture',
          metadata: { originalError: error, sentryError },
        });
      }
    }

    // Handle 401 Unauthorized with session-aware logic
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Skip token refresh for public endpoints
      const isPublicEndpoint =
        originalRequest.url?.includes('/invite/') ||
        originalRequest.url?.includes('/org-invitations/') ||
        originalRequest.url?.includes('/login/') ||
        originalRequest.url?.includes('/register/') ||
        originalRequest.url?.includes('/set-password/') ||
        originalRequest.url?.includes('/check-user/');

      if (isPublicEndpoint) {
        return Promise.reject(error);
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        })
          .then(config => {
            return enhancedAxiosInstance(config as InternalAxiosRequestConfig);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Enable error suppression during token refresh
      SessionAwareErrorHandler.suppressErrorToasts = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Attempt token refresh
        const response = await axios.post(`${baseURL}/api/token/refresh/`, {
          refresh: refreshToken,
        });

        if (response.data.access) {
          // Update stored tokens
          localStorage.setItem('access_token', response.data.access);

          if (response.data.refresh) {
            localStorage.setItem('refresh_token', response.data.refresh);
          }

          // Update auth header for current request
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
          }

          // Process queued requests with new token
          processQueue(undefined, response.data.access);

          // Disable error suppression
          SessionAwareErrorHandler.suppressErrorToasts = false;
          SessionAwareErrorHandler.resetSessionState();

          // Retry original request
          return enhancedAxiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - handle session expiry
        processQueue(refreshError, undefined);

        // Clear tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('session_start');

        // Clear persisted form data to prevent stale data on re-login
        try {
          await formStatePersistence.clearAllFormStates();
        } catch (persistenceError) {
          logError('Failed to clear form state on session expiry', {
            component: 'enhancedAxiosInstance',
            action: 'clearFormState',
            metadata: { persistenceError },
          });
        }

        // Check if session has been inactive for too long (>8 hours)
        const sessionStart = localStorage.getItem('session_start');
        const sessionAge = sessionStart ? Date.now() - parseInt(sessionStart) : 0;
        const isVeryOldSession = sessionAge > 8 * 60 * 60 * 1000; // 8 hours

        // Only show dialog if not an old inactive session
        if (!SessionAwareErrorHandler.isInactive && !isVeryOldSession) {
          SessionAwareErrorHandler.showSessionExpiredDialog(
            'Your session has expired. Please log in again.'
          );
        }

        // Disable error suppression
        SessionAwareErrorHandler.suppressErrorToasts = false;

        // Redirect to login (immediately for old sessions, with delay for active sessions)
        const redirectDelay = SessionAwareErrorHandler.isInactive || isVeryOldSession ? 0 : 2000;
        setTimeout(() => {
          window.location.href = '/login';
        }, redirectDelay);

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors normally
    return Promise.reject(error);
  }
);

// Export the enhanced instance and error handler
export { SessionAwareErrorHandler };
export default enhancedAxiosInstance;

// Extend the axios config interface to include metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
      requestId: string;
    };
  }
}
